import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RecommendationReasons = () => {
  const algorithmFactors = [
    {
      title: 'Profils similaires',
      description: 'Basé sur votre type de partenaire et vos spécialités musicales',
      weight: '25%',
      icon: 'Users',
      color: 'text-blue-600'
    },
    {
      title: 'Localisation géographique', 
      description: 'Professionnels dans votre région ou vos zones d\'intérêt',
      weight: '20%',
      icon: 'MapPin',
      color: 'text-green-600'
    },
    {
      title: 'Activité sur la plateforme',
      description: 'Utilisateurs actifs avec un historique de collaborations',
      weight: '20%',
      icon: 'Activity',
      color: 'text-orange-600'
    },
    {
      title: 'Connexions mutuelles',
      description: 'Personnes connectées avec vos contacts existants',
      weight: '15%',
      icon: 'Network',
      color: 'text-purple-600'
    },
    {
      title: 'Complémentarité',
      description: 'Profils qui complètent vos compétences et services',
      weight: '20%',
      icon: 'Puzzle',
      color: 'text-pink-600'
    }
  ];

  const networkingTips = [
    {
      title: 'Message personnalisé',
      description: 'Mentionnez un point commun ou un intérêt spécifique',
      icon: 'MessageSquare'
    },
    {
      title: 'Timing optimal',
      description: 'Envoyez vos demandes en semaine, entre 9h et 17h',
      icon: 'Clock'
    },
    {
      title: 'Profil complet',
      description: 'Un profil détaillé augmente vos chances de connexion',
      icon: 'User'
    },
    {
      title: 'Portfolio à jour',
      description: 'Montrez vos derniers travaux et réalisations',
      icon: 'Folder'
    }
  ];

  const qualityIndicators = [
    { label: 'Profil vérifié', description: 'Documents d\'identité confirmés', badge: '✓', color: 'text-green-600' },
    { label: 'Portfolio complet', description: 'Médias et échantillons de travail', badge: '📁', color: 'text-blue-600' },
    { label: 'Évaluations positives', description: 'Notes et commentaires clients', badge: '⭐', color: 'text-yellow-600' },
    { label: 'Activité récente', description: 'Connexion dans les 30 derniers jours', badge: '🟢', color: 'text-green-600' },
    { label: 'Réseaux sociaux liés', description: 'Présence sur les plateformes musicales', badge: '🔗', color: 'text-purple-600' }
  ];

  return (
    <div className="space-y-6">
      {/* How Recommendations Work */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Icon name="Brain" size={18} className="text-accent" />
          <h3 className="text-lg font-semibold text-foreground">Comment ça marche ?</h3>
        </div>
        
        <p className="text-sm text-muted-foreground mb-4">
          Nos recommandations sont basées sur plusieurs facteurs pour vous proposer les connexions les plus pertinentes :
        </p>

        <div className="space-y-3">
          {algorithmFactors?.map((factor, index) => (
            <div key={index} className="flex items-start space-x-3">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-muted ${factor?.color} flex-shrink-0`}>
                <Icon name={factor?.icon} size={16} />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="text-sm font-medium text-foreground">{factor?.title}</h4>
                  <span className="text-xs text-muted-foreground font-mono">{factor?.weight}</span>
                </div>
                <p className="text-xs text-muted-foreground">{factor?.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quality Indicators */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Icon name="Award" size={18} className="text-yellow-500" />
          <h3 className="text-lg font-semibold text-foreground">Indicateurs de qualité</h3>
        </div>
        
        <p className="text-sm text-muted-foreground mb-4">
          Recherchez ces signes pour identifier les profils de confiance :
        </p>

        <div className="space-y-3">
          {qualityIndicators?.map((indicator, index) => (
            <div key={index} className="flex items-start space-x-3 p-2 hover:bg-muted rounded-lg transition-colors">
              <span className="text-lg flex-shrink-0">{indicator?.badge}</span>
              <div>
                <h4 className={`text-sm font-medium ${indicator?.color}`}>
                  {indicator?.label}
                </h4>
                <p className="text-xs text-muted-foreground">{indicator?.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Networking Best Practices */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Icon name="Target" size={18} className="text-green-500" />
          <h3 className="text-lg font-semibold text-foreground">Bonnes pratiques</h3>
        </div>

        <div className="space-y-4">
          {networkingTips?.map((tip, index) => (
            <div key={index} className="flex items-start space-x-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-green-100 text-green-600 flex-shrink-0">
                <Icon name={tip?.icon} size={16} />
              </div>
              <div>
                <h4 className="text-sm font-medium text-foreground mb-1">
                  {tip?.title}
                </h4>
                <p className="text-xs text-muted-foreground">{tip?.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 p-4 bg-accent/10 border border-accent/20 rounded-lg">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={16} className="text-accent flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-accent-foreground mb-1">
                Astuce Pro
              </h4>
              <p className="text-xs text-muted-foreground mb-3">
                Les utilisateurs qui personnalisent leurs demandes de connexion ont un taux d'acceptation 65% plus élevé.
              </p>
              <Button variant="outline" size="xs">
                Voir des exemples de messages
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Help & Support */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Icon name="HelpCircle" size={18} className="text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground">Besoin d'aide ?</h3>
        </div>

        <div className="space-y-3">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-muted-foreground hover:text-foreground"
            iconName="BookOpen"
          >
            Guide du networking
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-muted-foreground hover:text-foreground"
            iconName="Video"
          >
            Tutoriels vidéo
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-muted-foreground hover:text-foreground"
            iconName="MessageCircle"
          >
            Contacter le support
          </Button>
        </div>

        <div className="mt-4 pt-4 border-t border-border text-center">
          <p className="text-xs text-muted-foreground">
            Des questions sur les recommandations ?
          </p>
          <Button variant="link" size="xs" className="mt-1">
            Envoyer un feedback
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RecommendationReasons;